/**
 * Feature Flag Scope Badge Component
 *
 * Displays the scope of a feature flag with distinctive color coding.
 * - Global: Blue - affects all users
 * - Plan: Violet - affects specific plans
 * - Tenant: Amber - affects specific tenants
 */
import { cn } from '@respectly/ui';
import { Globe, Layers, Building2 } from 'lucide-react';
import type { FeatureFlagScope } from '@respectly/shared-types';

// ============================================
// Scope Configuration
// ============================================

const SCOPE_CONFIG: Record<
  FeatureFlagScope,
  {
    label: string;
    description: string;
    bgColor: string;
    textColor: string;
    borderColor: string;
    icon: typeof Globe;
  }
> = {
  global: {
    label: 'Global',
    description: 'Affects all users',
    bgColor: 'bg-blue-50 dark:bg-blue-950/40',
    textColor: 'text-blue-700 dark:text-blue-300',
    borderColor: 'border-blue-200 dark:border-blue-800/60',
    icon: Globe,
  },
  plan: {
    label: 'Plan',
    description: 'Affects specific plans',
    bgColor: 'bg-violet-50 dark:bg-violet-950/40',
    textColor: 'text-violet-700 dark:text-violet-300',
    borderColor: 'border-violet-200 dark:border-violet-800/60',
    icon: Layers,
  },
  tenant: {
    label: 'Tenant',
    description: 'Affects specific tenants',
    bgColor: 'bg-amber-50 dark:bg-amber-950/40',
    textColor: 'text-amber-700 dark:text-amber-300',
    borderColor: 'border-amber-200 dark:border-amber-800/60',
    icon: Building2,
  },
};

// ============================================
// Size Configuration
// ============================================

const SIZE_STYLES = {
  sm: {
    container: 'px-2 py-0.5 text-xs gap-1',
    icon: 'w-3 h-3',
  },
  md: {
    container: 'px-2.5 py-1 text-sm gap-1.5',
    icon: 'w-3.5 h-3.5',
  },
};

// ============================================
// Component Props
// ============================================

export interface FeatureFlagScopeBadgeProps {
  scope: FeatureFlagScope;
  size?: 'sm' | 'md';
  showIcon?: boolean;
  className?: string;
}

// ============================================
// Component
// ============================================

export function FeatureFlagScopeBadge({
  scope,
  size = 'sm',
  showIcon = true,
  className,
}: FeatureFlagScopeBadgeProps) {
  const config = SCOPE_CONFIG[scope];
  const sizeStyles = SIZE_STYLES[size];
  const Icon = config.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full border',
        'transition-colors duration-150',
        config.bgColor,
        config.textColor,
        config.borderColor,
        sizeStyles.container,
        className
      )}
      title={config.description}
    >
      {showIcon && <Icon className={cn('flex-shrink-0', sizeStyles.icon)} />}
      <span className="truncate">{config.label}</span>
    </span>
  );
}

// ============================================
// Utility Functions
// ============================================

/** Get the display label for a scope */
export function getScopeLabel(scope: FeatureFlagScope): string {
  return SCOPE_CONFIG[scope].label;
}

/** Get the description for a scope */
export function getScopeDescription(scope: FeatureFlagScope): string {
  return SCOPE_CONFIG[scope].description;
}

/** Get all scope options for select dropdowns */
export function getScopeOptions(): {
  value: FeatureFlagScope;
  label: string;
  description: string;
}[] {
  return (Object.keys(SCOPE_CONFIG) as FeatureFlagScope[]).map((scope) => ({
    value: scope,
    label: SCOPE_CONFIG[scope].label,
    description: SCOPE_CONFIG[scope].description,
  }));
}
